import React, { useState } from 'react';
import NumberBadge from './NumberBadge';
import {
  Plus,
  Search,
  Play,
  Pause,
  Copy,
  Trash2,
  Eye,
  Users,
  Send,
  CheckCircle,
  Clock,
  TrendingUp
} from 'lucide-react';
import { useListBroadcastsQuery } from '@/client/api/whatsapp/list-broadcasts.query';
import { Link } from 'react-router-dom';
import { Campaign } from '@/client/types/whatsapp/broadcast-campaigns';
import moment from 'moment';
import BroadcastDetailsModal from '@/client/components/admin/WhatsApp/BroadcastDetailsModal';
import { useDeleteBroadcastMutation } from '@/client/api/whatsapp/broadcast.mutation';
import DeleteBroadcastModal from '@/client/components/admin/WhatsApp/DeleteBroadcastModal';

const BroadcastCampaignsPage: React.FC = () => {
  const { data, isLoading, refetch } = useListBroadcastsQuery();
  const campaigns = data?.data?.campaigns || [];

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [open, setOpen] = useState(false);

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || campaign.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<Campaign | null>(null);

  // delete handler
  const handleDeleteClick = (campaign: Campaign) => {
    setCampaignToDelete(campaign);
    setDeleteModalOpen(true);
  };

  // delete success handler
  const handleDeleteSuccess = () => {
    setDeleteModalOpen(false);
    setCampaignToDelete(null);
    refetch()
  };
  const getStatusBadge = (status: string) => {
    const styles = {
      completed: 'bg-green-100 text-green-700 border-green-200',
      in_progress: 'bg-cyan-100 text-cyan-700 border-cyan-200',
      paused: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      active: 'bg-blue-100 text-blue-700 border-blue-200',
      scheduled: 'bg-purple-100 text-purple-700 border-purple-200',
      canceled: 'bg-red-100 text-red-700 border-red-200'
    };
    return styles[status as keyof typeof styles] || styles.active;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <Play className="w-3 h-3" />;
      case 'paused':
        return <Pause className="w-3 h-3" />;
      case 'completed':
        return <CheckCircle className="w-3 h-3" />;
      case 'scheduled':
        return <Clock className="w-3 h-3" />;
      default:
        return null;
    }
  };

  const calculateDeliveryRate = (campaign: Campaign) => {
    if (campaign.sent === 0) return 0;
    return ((campaign.total || 0) / (campaign.sent || 1) * 100).toFixed(1);
  };

  const calculateReadRate = (campaign: Campaign) => {
    if (!campaign.total || campaign.total === 0) return 0;
    return ((campaign.sent || 0) / (campaign.total || 1) * 100).toFixed(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Broadcast Campaigns</h1>
            <p className="text-sm text-gray-500 mt-1">Manage and monitor your WhatsApp broadcast campaigns</p>
          </div>
          <Link
            to="/admin/relationship/whatsapp/broadcasts/new"
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Campaign
          </Link>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Total Campaigns</span>
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{campaigns.length}</p>
            <p className="text-xs text-gray-500 mt-1">All time</p>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Active Campaigns</span>
              <Play className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {campaigns.filter(c => c.status === 'active').length}
            </p>
            <p className="text-xs text-gray-500 mt-1">Currently running</p>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Total Sent</span>
              <Send className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {campaigns.reduce((sum, c) => sum + c.sent, 0)?.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 mt-1">Messages delivered</p>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Avg. Read Rate</span>
              <TrendingUp className="w-5 h-5 text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {(campaigns.reduce((sum, c) => sum + (c.total > 0 ? (c.sent / c.total) * 100 : 0), 0) / campaigns.length).toFixed(1)}%
            </p>
            <p className="text-xs text-gray-500 mt-1">Across all campaigns</p>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search campaigns..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
                <option value="scheduled">Scheduled</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recipients
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sent / Delivered
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Read Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Failed
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCampaigns.map((campaign) => (
                  <tr key={campaign.campaign_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{campaign.name.replace("_", " ")}</p>
                        {campaign.template_name && (
                          <p className="text-xs text-gray-500">Template: {campaign.template_name}</p>
                        )}
                        <div className="mt-1">
                          <NumberBadge id={campaign.from_number != null ? String(campaign.from_number) : undefined} />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(campaign.status)}`}>
                        {getStatusIcon(campaign.status)}
                        {campaign.status.charAt(0)?.toUpperCase() + campaign.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {campaign.total?.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <p className="text-gray-900">{campaign.sent?.toLocaleString()} / {campaign.total?.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">{calculateDeliveryRate(campaign)}% delivered</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2 w-20">
                          <div
                            className="bg-green-500 h-2 rounded-full"
                            style={{ width: `${calculateReadRate(campaign)}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-900">{calculateReadRate(campaign)}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm ${campaign.failed > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                        {campaign.failed}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {campaign.created_at ? `Created: ${moment.unix(campaign.created_at).format('YYYY-MM-DD HH:mm:ss')}` : ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button className="p-1 hover:bg-gray-100 rounded transition-colors"
                          onClick={() => {
                            setSelectedCampaign(campaign)
                            setOpen(true)
                          }}
                        >
                          <Eye className="w-4 h-4 text-gray-600" />
                        </button>

                        <button className="p-1 hover:bg-gray-100 rounded transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(campaign);
                          }}
                          title="Delete campaign"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>



          {isLoading ? (
            <div className="text-center py-12">
              <Send className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading...</h3>
              <p className="text-sm text-gray-500">Please wait...</p>
            </div>
          ) : filteredCampaigns.length === 0 && (
            <div className="text-center py-12">
              <Send className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No campaigns found</h3>
              <p className="text-sm text-gray-500">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </div>

      {campaignToDelete && (
        <DeleteBroadcastModal
          open={deleteModalOpen}
          onOpenChange={setDeleteModalOpen}
          campaignId={campaignToDelete.campaign_id}
          campaignName={campaignToDelete.name.replace("_", " ") || 'Unnamed Campaign'}
          onDeleteSuccess={handleDeleteSuccess}
        />
      )}
      {selectedCampaign && (
        <BroadcastDetailsModal
          open={open}
          onOpenChange={() => setOpen(!open)}
          onClose={() => {
            setOpen(false)
            setSelectedCampaign(null)
          }}
          broadcastId={selectedCampaign?.campaign_id}
        />)}
    </div>
  );
};

export default BroadcastCampaignsPage;
